import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { RegistrationForm } from '../components/RegistrationForm';
import { usePortalSiteStore } from '../stores/portalSiteStore';

/**
 * Standalone public registration page, reached from the storefront's
 * Register links, marketing CTAs ("Become a customer →") and email links.
 * The form is the brand's registration form in Workd Forms (PD-16574); the
 * page shows when the brand has an active one.
 *
 * Renders fully chrome-less (no PortalLayout): brand image fills the
 * background, the form floats on a translucent panel. Closes the loop
 * with a Sign-In link in case the visitor already has an account and
 * landed on /register by mistake.
 */
export function RegisterPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const site = usePortalSiteStore((s) => s.site);

	const isRegistrationEnabled = site?.isRegistrationEnabled ?? false;
	const loginBackgroundUrl = site?.loginBackgroundUrl ?? null;
	const brandLogoUrl = site?.logoUrl ?? null;
	const brandLogoUrlLight = site?.logoUrlLight ?? null;
	const loginTheme = site?.loginTheme ?? 'dark';
	const displayName = site?.displayName ?? null;

	const hasBackground = loginBackgroundUrl != null && loginBackgroundUrl !== '';

	// The /register page is a PRE-LOGIN surface, so — like HomePage and the
	// LoginModal — it must honor the brand's `loginTheme`. Previously it
	// hardcoded a light (opaque-white) panel and always used the dark-context
	// `logoUrl`; a dark-theme brand whose only wordmark is the white-on-dark
	// logo (e.g. Auvis) then rendered white-on-white (PD-14331). Dark theme →
	// dark glass panel, white text, the white `logoUrl`. Light theme → white
	// panel, dark text, the `logoUrlLight` (dark-ink) variant, falling back to
	// `logoUrl` when a brand hasn't uploaded a separate light wordmark.
	const isLight = loginTheme === 'light';
	const effectiveLogoUrl = isLight
		? (brandLogoUrlLight ?? brandLogoUrl)
		: brandLogoUrl;

	if (!isRegistrationEnabled) {
		return (
			<div className="min-h-dvh flex items-center justify-center bg-brand-page-bg px-4">
				<div className="max-w-md text-center">
					<h1 className="text-xl font-semibold text-default mb-2">
						{t('Registration is not currently available.')}
					</h1>
					<p className="text-sm text-brand-muted mb-6">
						{t(
							'Please contact your sales representative to set up an account, or try again later.',
						)}
					</p>
					<button
						type="button"
						onClick={() => {
							void navigate('/');
						}}
						className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-portal-accent hover:bg-portal-accent-hover text-white text-sm font-semibold transition-colors"
					>
						{t('Return to storefront')}
					</button>
				</div>
			</div>
		);
	}

	return (
		<div
			className={`relative min-h-dvh ${
				isLight ? 'bg-surface' : 'bg-brand-login-bg'
			}`}
		>
			{/* Backdrop image — fixed to the viewport, so it stays behind the
			    panel while a long form scrolls (with the band pinned to the top of
			    the page it scrolled away and the solid wrapper color showed below
			    it). Fixed positioning, not `background-attachment: fixed`, which
			    the iOS WebView ignores. The wrapper's `bg-brand-login-bg` (the
			    brand primary in both modes, PD-16045) still shows while the image
			    loads, and the page never reveals whatever is behind it. */}
			{hasBackground && (
				<div
					aria-hidden="true"
					className="fixed inset-0 overflow-hidden pointer-events-none"
				>
					<img
						src={loginBackgroundUrl}
						alt=""
						className="absolute inset-0 h-full w-full object-cover"
					/>
					{/* Scrim over the brand backdrop, matched to the panel: a
					    dark gradient for the dark-theme glass panel (keeps the
					    white wordmark + white text legible) and a light gradient
					    for the light-theme white panel (so a photo doesn't fight
					    the dark-ink content). */}
					<div
						className={
							isLight
								? 'absolute inset-0 bg-gradient-to-br from-white/75 via-white/55 to-white/75 backdrop-blur-[1px]'
								: 'absolute inset-0 bg-gradient-to-br from-black/60 via-black/45 to-black/60 backdrop-blur-[1px]'
						}
					/>
				</div>
			)}

			<div className="relative z-10 min-h-dvh flex items-start md:items-center justify-center px-4 py-12">
				<div
					className={`w-full max-w-2xl rounded-2xl shadow-2xl shadow-black/40 p-6 sm:p-10 ${
						isLight
							? 'bg-brand-surface'
							: 'bg-black/40 ring-1 ring-white/15 backdrop-blur-md'
					}`}
				>
					<div className="text-center mb-6">
						{effectiveLogoUrl != null && effectiveLogoUrl !== '' ? (
							<div className="mb-3 flex justify-center">
								<img
									src={effectiveLogoUrl}
									alt={displayName ?? t('Brand logo')}
									className="h-12 sm:h-14 object-contain"
								/>
							</div>
						) : (
							<h1
								className={`text-2xl font-bold ${
									isLight ? 'text-default' : 'text-white'
								}`}
							>
								{t(displayName ?? 'Become a customer')}
							</h1>
						)}
						<p
							className={`mt-2 text-sm ${
								isLight ? 'text-brand-muted' : 'text-white/70'
							}`}
						>
							{t(
								'Tell us about your business and we will set up your portal access.',
							)}
						</p>
					</div>

					<RegistrationForm
						variant={isLight ? 'page' : 'modal'}
						onComplete={() => {
							void navigate('/');
						}}
					/>

					<p
						className={`mt-8 text-center text-xs ${
							isLight ? 'text-brand-muted' : 'text-white/60'
						}`}
					>
						{t('Already have an account?')}{' '}
						<button
							type="button"
							onClick={() => {
								void navigate('/');
							}}
							className="text-portal-accent hover:underline font-medium"
						>
							{t('Sign in')}
						</button>
					</p>
				</div>
			</div>
		</div>
	);
}
